import React, { useState } from 'react';
import { useRouter } from 'next/router';

import { authMutations } from '@blockworks/session/mutations';
import { Alert, type AlertProps, Button, FlexBox } from '@blockworks/ui/components';
import { Form } from '@blockworks/ui/forms/form';

import AuthStepHeader from '@/components/auth/auth-step-header';
import { forgotPasswordSchema } from '@/components/auth/forms/forgot-password.schema';
import { AuthLayout } from '@/layout/auth/auth-layout';
import { IMetaProps } from '@/layout/default-meta';

export const getServerSideProps = async () => ({
    props: {
        meta: {
            title: 'Forgot Password | Blockworks Research',
        },
    },
});

const ForgotPasswordPage = () => {
    const router = useRouter();
    const [alert, setAlert] = useState<AlertProps>();
    const { handleSubmit, formState, control, reset } = Form.use({
        values: { email: '' },
        resolver: Form.resolvers.yup(forgotPasswordSchema),
    });

    const requestNewPassword = authMutations.requestNewPassword.use({
        onError: error => {
            reset({}, { keepSubmitCount: false });
            setAlert({
                intent: 'danger',
                children: `Something went wrong please try again.`,
            });
            console.error(error);
        },
        onSuccess: () => {
            setAlert({
                intent: 'success',
                children: `Password reset link sent. If you don’t receive it in the next 10 minutes you can try again.`,
            });
            setTimeout(() => {
                setAlert(undefined);
                reset({}, { keepSubmitCount: false });
            }, 120 * 1000);
        },
    });

    const onSubmit = handleSubmit(async ({ email }) => {
        requestNewPassword.mutate({ email });
    });

    const submitted = formState.submitCount > 0 && requestNewPassword.status === 'success';

    return (
        <FlexBox gap="md" col flexGrow={1}>
            <AuthStepHeader
                title="Forgot your password?"
                description="Enter your email address and we’ll send you a link to reset your password."
            >
                <AuthStepHeader.BackButton onClick={() => router.push('/sign-in')} />
            </AuthStepHeader>
            {alert && <Alert fontSize="xs" {...alert} />}

            <form
                onSubmit={e => {
                    e.preventDefault();
                    onSubmit();
                }}
            >
                <FlexBox gap="md" col>
                    <Form.Input name="email" control={control} type="email" />
                    <Button intent="primary" center disabled={submitted} loading={requestNewPassword.isPending}>
                        Reset password
                    </Button>
                </FlexBox>
            </form>
        </FlexBox>
    );
};

ForgotPasswordPage.getLayout = (page: React.ReactElement, metaProps: IMetaProps) => (
    <AuthLayout metaProps={metaProps} headingText="" subheadingText="">
        {page}
    </AuthLayout>
);

export default ForgotPasswordPage;
